import React from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { Facebook, Instagram } from 'lucide-react';
import { SiteFooter } from '../SiteFooter';

export const AppFooter = ({ dark = false }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const links = [
    { label: 'Home', path: user ? '/dashboard' : '/' },
    { label: 'Shop', path: '/shop' },
    { label: 'Community', path: '/community' },
    { label: 'Feature', path: '/spotlight' },
    { label: 'Events', path: '/events/discover' },
    { label: 'Blog', path: '/blog' },
    { label: 'Support', path: '/support' },
  ];
  return (
    <footer
      data-testid="app-footer"
      role="navigation"
      aria-label="Quick links"
      className={`fixed bottom-0 left-0 right-0 z-40 border-t backdrop-blur pb-[env(safe-area-inset-bottom)] ${dark ? 'bg-[#0f0f17] sm:bg-[#0f0f17]/90 border-white/10 text-slate-300' : 'bg-white sm:bg-white/95 border-gray-200 text-gray-600'}`}
    >
      <div className="container mx-auto px-3 sm:px-4 h-11 flex items-center justify-between gap-2 text-xs sm:text-sm">
        <button onClick={() => navigate(user ? '/dashboard' : '/')} className="font-bold flex items-center gap-1.5 shrink-0" data-testid="footer-brand">
          <span className={dark ? 'text-white' : 'bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent'}>Tapn.vip<span className="align-super text-[9px]">®</span></span>
          <span className={`text-[9px] sm:text-[10px] font-medium truncate max-w-[92px] sm:max-w-none ${dark ? 'text-slate-400' : 'text-gray-500'}`} data-testid="footer-llc-line">by Glossy Byrd Management LLC</span>
        </button>
        <div className="flex-1 min-w-0 flex items-center gap-3 sm:gap-5 font-semibold overflow-x-auto whitespace-nowrap justify-start sm:justify-end [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&>*]:shrink-0">
          {!user && (
            <Link to="/register" className="text-amber-400 hover:text-amber-300 font-bold" data-testid="footer-link-signup">
              Join Free
            </Link>
          )}
          {links.map((l) => (
            <Link key={l.label} to={l.path} className="hover:underline" data-testid={`footer-link-${l.label.toLowerCase()}`}>
              {l.label}
            </Link>
          ))}
          <Link to="/search" className="hover:underline opacity-70" data-testid="footer-link-search">Search</Link>
          <Link to="/privacy" className="hidden sm:inline hover:underline opacity-70" data-testid="footer-link-privacy">Privacy</Link>
          <Link to="/sitemap" className="hover:underline opacity-70" data-testid="footer-link-sitemap">Sitemap</Link>
          <Link to="/trust" className="hover:underline opacity-70" data-testid="footer-link-trust">Trust</Link>
          <Link to="/guides" className="hover:underline opacity-70" data-testid="footer-link-guides">How It Works</Link>
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/TapnVip_Offerings_Catalog.pdf`} target="_blank" rel="noopener noreferrer" className="hover:underline opacity-70" data-testid="footer-link-catalog">What We Offer</a>
          <LanguageSwitcher dark={dark} />
          <a href="https://www.facebook.com/share/1GV4agYCCT/" target="_blank" rel="noopener noreferrer" aria-label="Tapn.vip on Facebook" className={`${dark ? 'text-slate-300 hover:text-white' : 'text-gray-500 hover:text-blue-600'}`} data-testid="footer-facebook-link">
            <Facebook className="w-4 h-4" />
          </a>
          <a href="https://www.instagram.com/tapnvip" target="_blank" rel="noopener noreferrer" aria-label="Tapn.vip on Instagram" className={`${dark ? 'text-slate-300 hover:text-white' : 'text-gray-500 hover:text-pink-600'}`} data-testid="footer-instagram-link">
            <Instagram className="w-4 h-4" />
          </a>
        </div>
      </div>
    </footer>
  );
};

export const GlobalFooter = () => {
  const { pathname } = useLocation();
  const hidden = pathname === '/reels' || pathname === '/messages' || pathname.startsWith('/p/') || /^\/courses\/[^/]+\/learn$/.test(pathname);
  if (hidden) return null;
  return (
    <>
      {isPublicMarketingPath(pathname) && <SiteFooter />}
      <div className="h-11" aria-hidden />
      <AppFooter dark />
    </>
  );
};

const PUBLIC_EXACT = new Set(['/', '/about', '/press', '/pay', '/pricing', '/shop', '/blog', '/guides', '/trust', '/security', '/privacy', '/terms',
  '/disclaimers', '/refund-policy', '/cookie-policy', '/accessibility', '/dmca', '/support', '/compare', '/install', '/free-nfc-writer',
  '/sitemap', '/digital-identity', '/health-institutions', '/institutional', '/white-label', '/advertise', '/graduates', '/leaderboard',
  '/search', '/vault-plans', '/bundle', '/courses', '/events/discover', '/terms-team-member', '/hipaa', '/welcome-tour', '/nfc-test', '/franchise/apply']);
const PUBLIC_PREFIX = ['/for/', '/blog/'];
export const isPublicMarketingPath = (p) => PUBLIC_EXACT.has(p) || PUBLIC_PREFIX.some((x) => p.startsWith(x));

export default AppFooter;
